'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
  fetchMainThread,
  getLastSeenSeq,
  setLastSeenSeq,
  maxSeqOf,
  countUnread,
} from '@/lib/nexus-personal';

const PersonalAIContext = createContext(null);

// Ungelesen-Zaehler am Header-Button: auch bei geschlossenem Panel.
const UNREAD_POLL_MS = 60_000;
// Deeplink (Push web_route): ?nexus_personal=open oeffnet das Panel.
const DEEPLINK_PARAM = 'nexus_personal';

/**
 * PersonalAIProvider
 * Manages shared state for the Nexus-Personal panel (header Sparkles button).
 * Access-Check obsolet entfernt (2026-07-19) — Rechte via Rechtegruppen-Enforcement.
 * Listens for 'personal-ai:toggle' CustomEvent from header button.
 *
 * Nexus Personal (Phase 1+3): zusaetzlich
 *  - unreadCount: neue Assistent-Nachrichten im Hauptfaden seit dem letzten
 *    Oeffnen (last_seen_seq im localStorage), Polling alle 60s solange das
 *    Panel ZU ist (bei offenem Panel pollt das Panel selbst alle 20s).
 *  - markSeen(cid, seq): vom Panel aufgerufen, sobald Nachrichten sichtbar sind.
 *  - Deeplink ?nexus_personal=open → Panel oeffnen, Param aus der URL entfernen.
 */
export function PersonalAIProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  // OBSOLET entfernt (2026-07-19): der /api/ai/personal/access-check-Roundtrip
  // ist raus — Zugriff wird zentral über das Rechtegruppen-Enforcement gesteuert
  // (Rule: area-rights-enforcement.md). hasAccess bleibt fuer Consumer-Kompat true.
  const hasAccess = true;
  const pathname = usePathname();

  const [unreadCount, setUnreadCount] = useState(0);
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen(prev => !prev), []);

  // Listen for CustomEvent from header button (outside provider context)
  useEffect(() => {
    const handleToggle = () => setIsOpen(prev => !prev);
    window.addEventListener('personal-ai:toggle', handleToggle);
    return () => window.removeEventListener('personal-ai:toggle', handleToggle);
  }, []);

  const markSeen = useCallback((conversationId, seq) => {
    if (conversationId && seq != null) setLastSeenSeq(conversationId, seq);
    setUnreadCount(0);
  }, []);

  // Ungelesen-Polling (nur neueste Seite). Erstes Laden ohne last_seen setzt die
  // Marke auf den aktuellen Stand — der Badge zaehlt ab "jetzt", nicht die
  // gesamte Historie.
  useEffect(() => {
    let cancelled = false;
    // Nach 3 Fehlschlaegen in Folge (z.B. Endpunkt noch nicht deployt, kein
    // Recht) nicht weiter jede Minute Fehler in die Konsole schreiben.
    let failures = 0;
    const poll = async () => {
      if (isOpenRef.current || failures >= 3) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const page = await fetchMainThread();
        if (cancelled || !page || !page.conversation_id) return;
        const lastSeen = getLastSeenSeq(page.conversation_id);
        if (lastSeen == null) {
          const max = maxSeqOf(page.messages);
          setLastSeenSeq(page.conversation_id, max ?? -1);
          setUnreadCount(0);
          return;
        }
        setUnreadCount(countUnread(page.messages, lastSeen));
        failures = 0;
      } catch {
        failures += 1;
        // Best effort — der Badge ist Komfort, kein Blocker (z.B. Endpunkt noch nicht live).
      }
    };
    poll();
    const id = setInterval(poll, UNREAD_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Deeplink: bei jedem Routenwechsel + Browser-Navigation pruefen.
  useEffect(() => {
    const check = () => {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get(DEEPLINK_PARAM) !== 'open') return;
        setIsOpen(true);
        url.searchParams.delete(DEEPLINK_PARAM);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      } catch { /* ignore */ }
    };
    check();
    window.addEventListener('popstate', check);
    return () => window.removeEventListener('popstate', check);
  }, [pathname]);

  return (
    <PersonalAIContext.Provider value={{
      isOpen,
      hasAccess,
      openPanel,
      closePanel,
      togglePanel,
      unreadCount,
      markSeen,
    }}>
      {children}
    </PersonalAIContext.Provider>
  );
}

export function usePersonalAI() {
  const ctx = useContext(PersonalAIContext);
  if (!ctx) {
    throw new Error('usePersonalAI must be used within PersonalAIProvider');
  }
  return ctx;
}
